import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const CartContext = createContext(null);

const load = () => {
  try {
    return JSON.parse(localStorage.getItem("sparkle_cart") || "[]");
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("sparkle_cart", JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, qty = 1, opts = {}) => {
    setItems((prev) => {
      const key = `${product.id}|${opts.size || ""}|${opts.color || ""}`;
      // Ölçülü məhsulda limit SEÇİLMİŞ ölçünün stokudur (opts.stock),
      // ölçüsüz məhsulda isə məhsulun ümumi stoku.
      const stock = Number.isFinite(opts.stock) ? opts.stock : product.stock;
      // Səbətdəki miqdar heç vaxt stokdan çox ola bilməz. Server onsuz da
      // rədd edir, amma müştəri bunu son addımda yox, elə səbətdə görməlidir.
      const max = Number.isFinite(stock) ? stock : Infinity;
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          stock,
          quantity: Math.min(next[idx].quantity + qty, max),
        };
        return next;
      }
      return [
        ...prev,
        {
          key,
          id: product.id,
          name: product.name,
          name_en: product.name_en,
          price: product.price,
          image: product.images && product.images[0] ? product.images[0] : null,
          code: product.code,
          quantity: qty,
          size: opts.size || "",
          color: opts.color || "",
          stock,
        },
      ];
    });
    setOpen(true);
  }, []);

  const updateQty = useCallback((key, qty) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i.key !== key) return i;
        const max = Number.isFinite(i.stock) ? i.stock : Infinity;
        return { ...i, quantity: Math.min(Math.max(1, qty), max) };
      })
    );
  }, []);

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }, []);

  /**
   * Səbətdəki məhsulların stokunu serverdən yeniləyir.
   *
   * Səbət brauzerdə saxlanılır — məhsul o vaxtdan bəri satıla bilər.
   * Müştəri bunu ödəniş anında yox, elə səbətdə görməlidir.
   */
  const refreshStock = useCallback(async () => {
    const ids = [...new Set(items.map((i) => i.id))];
    if (!ids.length) return;

    const results = await Promise.all(
      ids.map((id) =>
        api
          .get(`/products/${id}`)
          .then(({ data }) => [id, data])
          // Məhsul silinibsə (404) satılmış kimi göstəririk.
          .catch(() => [id, null])
      )
    );
    const fresh = Object.fromEntries(results);

    // Ölçülü məhsulda səbətdəki sətrin stoku ümumi saydan yox, MƏHZ öz
    // ölçüsündən asılıdır: "M" bitibsə, "L" hələ də alına bilər.
    const stockOf = (item) => {
      const p = fresh[item.id];
      if (!p) return 0;
      const variants = p.variants || [];
      if (!variants.length) return p.stock ?? 0;
      return variants.find((v) => v.size === item.size)?.stock ?? 0;
    };

    setItems((prev) =>
      prev.map((i) => {
        if (!(i.id in fresh)) return i;
        const stock = stockOf(i);
        return {
          ...i,
          stock,
          // Stok azalıbsa miqdarı da endiririk (0-dırsa 1-də saxlayırıq ki,
          // "satılıb" yazısı düzgün görünsün).
          quantity: stock > 0 ? Math.min(i.quantity, stock) : i.quantity,
        };
      })
    );
  }, [items]);

  // Səbətdə satılmış məhsul varsa sifariş verilə bilməz.
  const hasSoldOut = items.some((i) => (i.stock ?? 0) <= 0);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  // Satılmış məhsul cəmə daxil edilmir — müştəri onu onsuz da ala bilməyəcək.
  const subtotal = items.reduce(
    (s, i) => ((i.stock ?? 0) > 0 ? s + i.price * i.quantity : s),
    0
  );

  return (
    <CartContext.Provider
      value={{ items, open, setOpen, addItem, updateQty, removeItem, clear,
               count, subtotal, refreshStock, hasSoldOut }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
